import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Bell, LogOut, Menu, X, LayoutDashboard, Briefcase, Bot, UserCircle,
  Activity, ShieldCheck, GitBranch, Inbox, Columns3, Settings, Archive, BookOpen,
  KeyRound, Fingerprint, HeartPulse, GitPullRequestArrow, ScrollText, Radio } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { container } from "@/lib/constants";

const investorNav = [
  { to: "/deals", label: "Deal Board", icon: Briefcase },
  { to: "/orla", label: "ORLA", icon: Bot },
  { to: "/account", label: "Account", icon: UserCircle },
  { to: "/notifications", label: "Notifications", icon: Bell },
];

const adminNav = [
  { to: "/admin", label: "Command Centre", icon: LayoutDashboard, end: true },
  { to: "/admin/activity-trail", label: "Activity Trail", icon: ScrollText },
  { to: "/admin/system-health", label: "System Health", icon: HeartPulse },
  { to: "/admin/repo-health", label: "Repo Health", icon: Activity },
  { to: "/admin/cross-review", label: "Cross-Review", icon: GitPullRequestArrow },
  { to: "/admin/orla", label: "ORLA Agents", icon: Bot },
  { to: "/admin/webhook-secrets", label: "Webhook Secrets", icon: KeyRound },
  { to: "/admin/quantus", label: "Quantus Integration", icon: Radio },
  { to: "/admin/secret-fingerprint", label: "Secret Fingerprint", icon: Fingerprint },
];

const bridgeNav = [
  { to: "/bridge", label: "Sourcing Inbox", icon: Inbox, end: true },
  { to: "/bridge/pipeline", label: "Pipeline", icon: Columns3 },
  { to: "/bridge/settings", label: "Settings", icon: Settings },
  { to: "/bridge/archive", label: "Archive", icon: Archive },
  { to: "/bridge/how-it-works", label: "How it works", icon: BookOpen },
];

function NavGroup({ title, items, onNavigate }) {
  return (
    <div className="mb-6">
      <div className="pdg-kicker px-3 mb-2 text-[10px]">{title}</div>
      <nav className="space-y-1">
        {items.map((it) => (
          <NavLink key={it.to} to={it.to} end={it.end} onClick={onNavigate}
            data-testid={`portal-nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g,"")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 h-10 rounded-lg text-sm transition-colors duration-200 ${
                isActive ? "bg-[hsl(var(--pdg-gold)/0.12)] text-[hsl(var(--pdg-gold))]"
                  : "text-foreground/70 hover:text-foreground hover:bg-[hsl(var(--pdg-elevated)/0.5)]"}`}>
            <it.icon size={17} strokeWidth={1.6} /> {it.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

export function PortalLayout({ children, title }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    api.get("/notifications").then((r) => setUnread(r.data.unread || 0)).catch(() => {});
  }, [location.pathname]);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const doLogout = async () => { await logout(); navigate("/"); };

  const Sidebar = ({ onNavigate }) => (
    <div className="h-full flex flex-col">
      <div className="px-3 py-5">
        <Link to="/" className="pdg-serif text-base tracking-[0.1em] text-foreground">PRIME DEAL GATEWAY</Link>
      </div>
      <div className="flex-1 overflow-y-auto px-2 pb-6">
        <NavGroup title="Investor" items={investorNav} onNavigate={onNavigate} />
        {isAdmin && <NavGroup title="Admin" items={adminNav} onNavigate={onNavigate} />}
        {isAdmin && <NavGroup title="Deal Bridge" items={bridgeNav} onNavigate={onNavigate} />}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-64 shrink-0 border-r border-border bg-[hsl(var(--pdg-charcoal))] sticky top-0 h-screen">
        <Sidebar />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 border-r border-border bg-[hsl(var(--pdg-charcoal))]">
            <Sidebar onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-40 h-16 border-b border-border bg-[hsl(var(--background)/0.8)] backdrop-blur-md flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-foreground" onClick={() => setOpen(!open)} data-testid="portal-mobile-toggle">
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
            <h1 className="pdg-serif text-xl text-foreground">{title}</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/notifications" className="relative text-foreground/70 hover:text-foreground" data-testid="portal-notif-bell">
              <Bell size={19} strokeWidth={1.6} />
              {unread > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-[hsl(var(--pdg-gold))] text-[hsl(var(--pdg-obsidian))] text-[10px] font-semibold flex items-center justify-center">{unread}</span>}
            </Link>
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-xs text-foreground/85">{user?.full_name}</span>
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{user?.role === "admin" || user?.role === "service" ? user.role : user?.tier}</span>
            </div>
            <button onClick={doLogout} data-testid="portal-logout-button" className="text-foreground/70 hover:text-[hsl(var(--pdg-gold))] transition-colors" title="Sign out">
              <LogOut size={18} strokeWidth={1.6} />
            </button>
          </div>
        </header>
        <main className="flex-1 pdg-fade-in">
          <div className={`${container} py-8`}>{children}</div>
        </main>
      </div>
    </div>
  );
}
